/**
 * FullTree.js
 * © EMETL Working Group
 * 
 * @author Andrew Berger
 */

import React, { 
    useEffect, 
    useState,
    useContext,
} from "react"

import {
    Container,
    Row,
    Col,
    Spinner,
    CloseButton,
    Button,
} from "react-bootstrap"

import { 
    Tree, 
    TreeNode, 
} from "react-organizational-chart"

import UnitTreeViewModel from "../Model/UnitTree.m"
import "./Stylesheets/App.css"

import {
    Context, 
    EMETLContext,
} from "../Model/Contexts/AppContext"

import { 
    Unit, 
} from "../API"

const UnitTreeVM = new UnitTreeViewModel()

let fullTreeStructure = {}

type FullTreeProps = {
    close: () => void,
}

export default function FullTree(props: FullTreeProps) {
    const {
        personnel: {
            value: dudes,
        },
    } = useContext(Context) as EMETLContext

    const { close } = props
    // const [_hoveredID, hoverID] = useState(undefined)
    const [selectedID, selectID] = useState(undefined)
    const [tree, setTree] = useState(undefined)
    const [rootNode, setRootNode] = useState(undefined)
    const [loading, setLoading] = useState(false)

    const onSelect = (unitID: number) => {
        selectID(unitID)
    }
    // const onHover = (unitID: number) => {
    //     hoverID(unitID)
    // }

    const processUnitTreeData = (treeMap) => {
        const t = printTree(treeMap, treeMap["0"], onSelect, () => {/**/})
        fullTreeStructure = treeMap
        setRootNode(treeMap["0"])
        setTree(t)
        setLoading(false)
    }

    const loadPersonnel = async (unit: Unit) => {
        const p = await UnitTreeVM.listPersonnelAtUnit(unit.ID, unit.Depth)
        if (!!p?.result?.length && p?.result) {
            try {
                const listPersonnelResponse = JSON.parse(p?.result)
                const result = listPersonnelResponse?.body?.result
                if (result && !!result?.length)
                    return result[0]

            } catch (e) {
                console.error(e)
            }
            
        }

        return false
    }

    useEffect(() => {
        setLoading(true)
        UnitTreeVM.loadFullTree(processUnitTreeData)
    }, [])

    const UnitDetail = ({ unit: { name, unitID, branches, depth }, unit }) => {
        const [dudesAtUnit, setDudesAtUnit] = useState([])

        const load = async () => {
            const res = await loadPersonnel({
                ID: unitID,
                Depth: depth,
                __typename: "Unit",
            })

            if (res) 
                setDudesAtUnit(res)
            else
                setDudesAtUnit([])
        }

        useEffect(() => {
            if (unitID && dudes)
                load()
            
        }, [unitID, dudes])

        return (
            <>
                <CloseButton onClick={() => selectID(null)} />
                <h5>Selected unit "{name}" [ID = {unitID}]</h5>
                <p>
                    Has Branches: {branches.map((bid, i) => (
                        <span key={bid}>{bid}{(i+1 < branches.length) ? ", " : ""}</span>
                    ))}
                </p>
                <p>Depth: { depth }</p>
                {!!dudesAtUnit?.length && (
                    <div>
                        <h6>Personnel at this unit:</h6>
                        <ul>
                            {
                                dudesAtUnit.map((dude) => (
                                    <li key={dude.ID}>{dude.Name} ({dude.Email}) - {dude.ID}</li>
                                ))
                            }
                        </ul>
                    </div>
                )}
            </>
        )
    }

    return (
        <Container
            className="w-75"
        >
            {loading ? <div className="spinnerBox"><Spinner animation="border" role="status" /></div> : (
                <>
                    <Row
                        style={{
                            overflow: "scroll",
                        }}
                    >
                        {tree && rootNode && 
                            (
                                <Tree
                                    label={                                
                                        <h4>{rootNode.name}</h4>
                                    }
                                >
                                    {tree && tree}
                                </Tree>
                            )
                        }
                    </Row>
                    <Row>
                        <Col>
                            {
                                selectedID && fullTreeStructure[selectedID] ?
                                    <UnitDetail unit={fullTreeStructure[selectedID]} /> :
                                    <h5>Select a unit to display information</h5>
                            }
                        </Col>
                    </Row>
                    <Row>
                        <Button onClick={close}>Close Full Tree View</Button>
                    </Row>
                </>
            )}
        </Container>
    )
}

const printTree = (fullTree, parent, onSelect, onHover) => {
    return parent.unitID !== 0 ? (
        <UnitNode 
            unitID = {parent.unitID}
            name = {parent.name}
            onSelect = {onSelect}
            onHover = {onHover}
            key={parent.name}
        >
            {
                parent.immediateChildren.map(
                    child => {
                        return printTree(fullTree, fullTree[child], onSelect, onHover)
                    }
                )
            }
        </UnitNode>
    ) : parent.immediateChildren.map(
        child => {
            return printTree(fullTree, fullTree[child], onSelect, onHover)
        }
    )
}

/**
 * @param props
 */
function UnitNode(props) {
    const {
        unitID,
        name,
        onHover, 
        onSelect,
        selectedID,
        children
    } = props

    const [isHoveredOver, hoverOver] = useState(false)
    const [isSelected, select] = useState(false)    

    // ancestorMarkers.forEach((mark) => {
    //     mark(unit)
    // })
    const manageHover = (u) => {
        hoverOver(u)
        onHover(u)
    }
    return (
        <TreeNode 
            label={
                <UnitLabel
                    name={name}
                    hovered={isHoveredOver}
                    selected={unitID === selectedID}
                    onMouseEnter={() => {
                        manageHover(unitID)
                    }}
                    onMouseLeave={() => {
                        manageHover(null)
                    }}
                    onClick={() => {
                        select(unitID)
                        onSelect(unitID)
                    }}
                />
            }
        >
            {children}
        </TreeNode>
    )
}

/**
 * @param props
 */
function UnitLabel(props) {
    const {
        name,
        hovered,
        selected,
        onMouseEnter,
        onMouseLeave,
        onClick,
    } = props

    return (
        <div>
            <div
                style={{
                    cursor: "pointer",
                    display: "inline-block",
                    padding: "5px",
                    borderStyle: "solid",
                    borderColor: (hovered || selected) ? "red" : "black",
                    borderRadius: "5px",
                }}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={onClick}
            >
                <h5>{ name }</h5>
            </div>
            {
                // (chovered) ?
                // <span>{chovered.branch}</span> :
                // <span>&nbsp;</span>
            }
        </div>
    )
}